/**
 * AlertsPage — displays alerts in a FilterableDataTable with severity icons,
 * quick actions (acknowledge, resolve), and a "View Machine" navigation link.
 *
 * Requirements: 9.1, 9.2, 9.3, 9.4, 9.5, 9.6, 9.7, 9.8
 */

import React, { useState, useCallback, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { GridColDef } from '@mui/x-data-grid';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import ErrorIcon from '@mui/icons-material/Error';
import WarningIcon from '@mui/icons-material/Warning';
import InfoIcon from '@mui/icons-material/Info';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import VisibilityIcon from '@mui/icons-material/Visibility';

import {
  FilterableDataTable,
  FilterConfig,
  ActiveFilter,
  RowAction,
  PageContainer,
} from '../../components/shared';
import { useNotification } from '../../providers/NotificationProvider';
import { AlertsApi } from '../../api-generated/apis';
import { apiConfig } from '../../api-generated/apiConfig';

const alertsApi = new AlertsApi(apiConfig);

// ─── Types ───────────────────────────────────────────────────────────────────

type AlertSeverity = 'critical' | 'warning' | 'info';
type AlertStatus = 'active' | 'acknowledged' | 'resolved';

interface Alert {
  id: number;
  machineId: number;
  machineName: string;
  partName: string;
  severity: AlertSeverity;
  message: string;
  triggeredAt: string;
  acknowledgedAt: string | null;
  status: AlertStatus;
}

interface AlertsResponse {
  content: Alert[];
  totalElements: number;
}

// ─── Severity Rendering ──────────────────────────────────────────────────────

function renderSeverityIcon(severity: AlertSeverity): React.ReactNode {
  switch (severity) {
    case 'critical':
      return (
        <Tooltip title="Critical">
          <ErrorIcon sx={{ color: '#d32f2f' }} />
        </Tooltip>
      );
    case 'warning':
      return (
        <Tooltip title="Warning">
          <WarningIcon sx={{ color: '#ed6c02' }} />
        </Tooltip>
      );
    case 'info':
      return (
        <Tooltip title="Info">
          <InfoIcon sx={{ color: '#0288d1' }} />
        </Tooltip>
      );
    default:
      return null;
  }
}

// ─── Severity sort order (critical first) ────────────────────────────────────

const SEVERITY_ORDER: Record<AlertSeverity, number> = {
  critical: 0,
  warning: 1,
  info: 2,
};

// ─── Filter Configuration ────────────────────────────────────────────────────

const ALERT_FILTERS: FilterConfig[] = [
  {
    field: 'severity',
    label: 'Severity',
    type: 'multiselect',
    options: [
      { value: 'critical', label: 'Critical' },
      { value: 'warning', label: 'Warning' },
      { value: 'info', label: 'Info' },
    ],
  },
  {
    field: 'status',
    label: 'Status',
    type: 'select',
    options: [
      { value: 'active', label: 'Active' },
      { value: 'acknowledged', label: 'Acknowledged' },
      { value: 'resolved', label: 'Resolved' },
    ],
  },
  {
    field: 'machineId',
    label: 'Machine',
    type: 'select',
    options: [], // Populated dynamically if needed; left empty for now
  },
];

// ─── Component ───────────────────────────────────────────────────────────────

const AlertsPage: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const notification = useNotification();

  // Pagination state
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);

  // Filter state
  const [machineFilter, setMachineFilter] = useState<string>('');

  // ─── Data fetching ─────────────────────────────────────────────────────

  const alertsQuery = useQuery<any, Error>({
    queryKey: ['alerts', page, pageSize, machineFilter],
    queryFn: () =>
      alertsApi.getActiveAlerts({
        machineId: machineFilter || undefined,
      }),
  });

  // Map the response to AlertsResponse shape
  const alertsData: AlertsResponse = useMemo(() => {
    const raw = alertsQuery.data;
    if (!raw) return { content: [], totalElements: 0 };
    // Handle both array and paginated response
    if (Array.isArray(raw)) {
      return { content: raw, totalElements: raw.length };
    }
    return {
      content: raw.content ?? raw.alerts ?? [],
      totalElements: raw.totalElements ?? raw.content?.length ?? 0,
    };
  }, [alertsQuery.data]);

  // ─── Mutations ─────────────────────────────────────────────────────────

  const acknowledgeMutation = useMutation({
    mutationFn: (id: number) => alertsApi.acknowledgeAlert({ id: String(id) }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['alerts'] });
      notification.showSuccess('Alert acknowledged.');
    },
    onError: (error: Error) => {
      notification.showError(`Failed to acknowledge: ${error.message}`);
    },
  });

  const resolveMutation = useMutation({
    mutationFn: (id: number) => alertsApi.resolveAlert({ id: String(id) }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['alerts'] });
      notification.showSuccess('Alert resolved.');
    },
    onError: (error: Error) => {
      notification.showError(`Failed to resolve: ${error.message}`);
    },
  });

  // ─── Filter change handler ─────────────────────────────────────────────

  const handleFilterChange = useCallback((filters: ActiveFilter[]) => {
    const machineActive = filters.find((f) => f.field === 'machineId');
    if (machineActive && typeof machineActive.value === 'string' && machineActive.value) {
      setMachineFilter(machineActive.value);
    } else {
      setMachineFilter('');
    }
  }, []);

  // ─── Columns ───────────────────────────────────────────────────────────

  const columns: GridColDef<Alert>[] = useMemo(
    () => [
      {
        field: 'severity',
        headerName: 'Severity',
        width: 100,
        sortable: true,
        sortComparator: (v1: AlertSeverity, v2: AlertSeverity) =>
          SEVERITY_ORDER[v1] - SEVERITY_ORDER[v2],
        renderCell: (params) => (
          <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
            {renderSeverityIcon(params.value as AlertSeverity)}
          </Box>
        ),
      },
      {
        field: 'machineName',
        headerName: 'Machine',
        flex: 1,
        minWidth: 140,
      },
      {
        field: 'partName',
        headerName: 'Part',
        flex: 1,
        minWidth: 120,
      },
      {
        field: 'message',
        headerName: 'Message',
        flex: 2,
        minWidth: 200,
      },
      {
        field: 'triggeredAt',
        headerName: 'Triggered',
        width: 180,
        renderCell: (params) => {
          if (!params.value) return '';
          return new Date(params.value as string).toLocaleString();
        },
      },
      {
        field: 'status',
        headerName: 'Status',
        width: 130,
        renderCell: (params) => {
          const status = params.value as AlertStatus;
          const colorMap: Record<AlertStatus, string> = {
            active: '#d32f2f',
            acknowledged: '#ed6c02',
            resolved: '#2e7d32',
          };
          return (
            <Typography
              variant="body2"
              sx={{
                color: colorMap[status],
                fontWeight: 500,
                textTransform: 'capitalize',
              }}
            >
              {status}
            </Typography>
          );
        },
      },
    ],
    [],
  );

  // ─── Row Actions ───────────────────────────────────────────────────────

  const rowActions: RowAction<Alert>[] = useMemo(
    () => [
      {
        label: 'Acknowledge',
        icon: CheckCircleIcon,
        onClick: (row: Alert) => acknowledgeMutation.mutate(row.id),
        show: (row: Alert) => row.status === 'active',
      },
      {
        label: 'Resolve',
        icon: DoneAllIcon,
        onClick: (row: Alert) => resolveMutation.mutate(row.id),
        show: (row: Alert) => row.status === 'active' || row.status === 'acknowledged',
      },
      {
        label: 'View Machine',
        icon: VisibilityIcon,
        onClick: (row: Alert) => navigate(`/machines/${row.machineId}`),
      },
    ],
    [acknowledgeMutation, resolveMutation, navigate],
  );

  // ─── Sorted rows (severity then triggered time) ────────────────────────

  const sortedRows = useMemo(() => {
    const rows: Alert[] = alertsData.content ?? [];
    return [...rows].sort((a: Alert, b: Alert) => {
      const sevDiff = SEVERITY_ORDER[a.severity] - SEVERITY_ORDER[b.severity];
      if (sevDiff !== 0) return sevDiff;
      return new Date(b.triggeredAt).getTime() - new Date(a.triggeredAt).getTime();
    });
  }, [alertsData]);

  // ─── Render ────────────────────────────────────────────────────────────

  return (
    <PageContainer title="Alerts">
      <FilterableDataTable<Alert>
        rows={sortedRows}
        columns={columns}
        loading={alertsQuery.isLoading}
        totalCount={alertsData.totalElements}
        searchPlaceholder="Search alerts..."
        filters={ALERT_FILTERS}
        onFilterChange={handleFilterChange}
        page={page}
        pageSize={pageSize}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
        actions={rowActions}
      />
    </PageContainer>
  );
};

export default AlertsPage;
